// FCM push notification registration for Capacitor (Android).
// Web Push is gone — see supabase/functions/send-push/index.ts for server side.

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '../supabase/config';

const isNative = () => Capacitor.isNativePlatform?.() === true;

let listenersAttached = false;
let lastUploadedToken = null;

const uploadFcmToken = async (token) => {
  if (!token || token === lastUploadedToken) return;
  const userId = window.currentAppUser?.uid || window.currentUser?.uid;
  if (!userId) return;

  const supabaseClient = window.supabase || supabase;
  const { error } = await supabaseClient
    .from('push_subscriptions')
    .upsert({
      user_id: userId,
      platform: 'android',
      fcm_token: token,
      subscription: null,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id' });

  if (error) {
    console.error('FCM token upload error:', error);
    return;
  }
  lastUploadedToken = token;
};

const attachListeners = () => {
  if (listenersAttached) return;
  listenersAttached = true;

  PushNotifications.addListener('registration', (token) => {
    // token.value is the FCM registration token
    uploadFcmToken(token.value);
  });

  PushNotifications.addListener('registrationError', (err) => {
    console.error('FCM registration error:', err);
  });

  // Fired when a push arrives while the app is in foreground.
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    const payload = {
      title: notification.title || notification.data?.title || 'Linksy',
      body: notification.body || notification.data?.body || '',
      type: notification.data?.type,
      eventId: notification.data?.eventId,
    };
    // Hand off to the existing in-app popup system if available.
    window.dispatchEvent(new CustomEvent('fcmForegroundPush', { detail: payload }));
  });

  // Fired when user taps a push (app cold-start or background → foreground).
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const data = action.notification?.data || {};
    if (data.eventId && window._openEventFromNotification) {
      // Slight delay so the React tree has time to mount on cold start.
      setTimeout(() => window._openEventFromNotification(data.eventId), 400);
    }
    if (data.openNotifications === 'true' && window._setNotificationsOpen) {
      setTimeout(() => window._setNotificationsOpen(true), 400);
    }
  });
};

export const registerFcmPush = async () => {
  if (!isNative()) return;

  attachListeners();

  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      console.warn('Push permission not granted:', perm.receive);
      return;
    }
    await PushNotifications.register();
  } catch (e) {
    console.error('registerFcmPush error:', e);
  }
};

export const unregisterFcmPush = async () => {
  if (!isNative()) return;
  try {
    const userId = window.currentAppUser?.uid || window.currentUser?.uid;
    if (userId) {
      const supabaseClient = window.supabase || supabase;
      await supabaseClient.from('push_subscriptions').delete().eq('user_id', userId);
    }
    lastUploadedToken = null;
  } catch (e) { console.error('unregisterFcmPush:', e); }
};

// Expose for notifications.js stub + logout flow
window._registerFcmPush = registerFcmPush;
window._unregisterFcmPush = unregisterFcmPush;
